import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, Sparkles, Check, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { track } from '@/lib/analytics';

interface NewsletterGateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Email validation - mirrors backend blocklist
const BLOCKED_DOMAINS = [
  'example.com', 'example.org', 'example.net', 'test.com', 'test.org', 'test.net',
  'fake.com', 'no.com', 'none.com', 'noemail.com', 'nowhere.com', 'nope.com',
  'mailinator.com', 'guerrillamail.com', 'tempmail.com', 'temp-mail.org',
  '10minutemail.com', 'throwaway.email', 'yopmail.com', 'trashmail.com',
  'sharklasers.com', 'getnada.com', 'maildrop.cc', 'fakeinbox.com'
];

const BLOCKED_LOCAL_PARTS = [
  'test', 'asdf', 'fake', 'none', 'no', 'spam', 'trash', 'abc', 'qwerty',
  'testing', 'temp', 'example', 'admin', 'user', 'demo', 'sample', 'noreply'
];

const validateEmail = (email: string): { valid: boolean; error?: string } => {
  const normalized = email.toLowerCase().trim();
  if (!normalized || !normalized.includes('@')) {
    return { valid: false, error: 'Please enter a valid email address' };
  }
  
  const [localPart, domain] = normalized.split('@');
  
  if (BLOCKED_DOMAINS.some(d => domain === d || domain.endsWith(`.${d}`))) {
    return { valid: false, error: 'Please use a real email address' };
  }
  
  if (BLOCKED_LOCAL_PARTS.includes(localPart) || /^test\d*$/i.test(localPart)) {
    return { valid: false, error: 'Please use a real email address' };
  }
  
  return { valid: true };
};

const extractErrorMessage = (error: unknown, data: unknown): string => {
  const dataObj = data as { error?: string } | null | undefined;
  const err = error as {
    message?: string;
    context?: { json?: { error?: string }; body?: string };
  } | null | undefined;
  // Check data.error first (direct response from edge function)
  if (dataObj?.error) return dataObj.error;
  
  // Check error.message for embedded JSON
  if (err?.message) {
    const msg = err.message;
    const jsonMatch = msg.match(/\{[^{}]*"error"[^{}]*\}/);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed.error) return parsed.error;
      } catch { /* noop */ }
    }
    if (msg.includes('real email')) return 'Please use a real email address';
    if (msg.includes('valid email')) return 'Please use a valid email address';
  }
  
  // Check error.context for response body
  if (err?.context?.json?.error) return err.context.json.error;
  if (err?.context?.body) {
    try {
      const parsed = JSON.parse(err.context.body);
      if (parsed.error) return parsed.error;
    } catch { /* noop */ }
  }
  
  return 'Something went wrong. Please try again.';
};

export const NewsletterGateModal: React.FC<NewsletterGateModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const emailValidation = useMemo(() => validateEmail(email), [email]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // Show validation error immediately if email contains @
    if (newEmail.includes('@')) {
      const validation = validateEmail(newEmail);
      if (!validation.valid) {
        setInlineError(validation.error || 'Please use a real email address');
      } else {
        setInlineError(null);
      }
    } else {
      setInlineError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setInlineError('Please enter your email');
      return;
    }

    if (!emailValidation.valid) {
      setInlineError(emailValidation.error || 'Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setInlineError(null);

    try {
      const { data, error } = await supabase.functions.invoke('subscribe-newsletter', {
        body: { email: email.trim(), source: 'calendar_gate' },
      });

      if (data?.error) {
        setInlineError(data.error);
        return;
      }

      if (error) {
        const errorMsg = extractErrorMessage(error, data);
        setInlineError(errorMsg);
        return;
      }

      setIsSuccess(true);
      track('newsletter_signup');
      toast.success('Welcome! Enjoy unlimited calendar access.');
      
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      const errorMsg = extractErrorMessage(error, null);
      setInlineError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        {isSuccess ? (
          <div className="flex flex-col items-center py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">You're subscribed!</h3>
            <p className="text-muted-foreground">Enjoy unlimited calendar access.</p>
          </div>
        ) : (
          <>
            <DialogHeader className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
              <DialogTitle className="text-xl">Unlock Unlimited Access</DialogTitle>
              <DialogDescription className="text-base">
                You've used your 3 free calendar previews. Subscribe to our newsletter for unlimited access!
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={handleEmailChange}
                    className={cn(
                      "pl-10",
                      inlineError && "border-destructive focus-visible:ring-destructive"
                    )}
                    disabled={isLoading}
                  />
                </div>
                {inlineError && (
                  <p className="text-sm text-destructive">{inlineError}</p>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading || (email.length > 0 && !emailValidation.valid)}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Subscribing...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Subscribe & Continue
                  </>
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <p className="text-xs text-muted-foreground">
                Get astrophotography tips, new feature updates, and more. Unsubscribe anytime.
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};